import { Controller } from "stimulus";
import { EmojiButton } from "@joeattardi/emoji-button";

export default class extends Controller {
  static targets = ["menu", "people", "feedback", "details"];

  connect() {
    this.list = this.element.querySelector("#message_headers");
    this.userId = Number(this.list.dataset.userId);
    this.detailsTarget.id = `chat-reaction-details-${this.list.dataset.groupId}`;
    this.requests = new Set();
    this.hydrate(this.list);
    this.observer = new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) this.hydrate(node);
      }));
      if (this.activeRoot && (!this.activeRoot.isConnected || this.activeRoot.classList.contains("message--deleted"))) this.close();
      if (this.detailsAnchor && !this.detailsAnchor.isConnected) this.hideReactionDetails();
    });
    this.observer.observe(this.list, { childList: true, subtree: true });
    this.pressStart = event => {
      if (!event.isPrimary || (event.pointerType !== "touch" && event.pointerType !== "pen")) return;
      if (event.target.closest("a, button, trix-editor")) return;
      const root = event.target.closest(".message_header-container");
      if (!root?.querySelector(".chat-reactions") || root.classList.contains("message--deleted")) return;
      this.pressOrigin = { x: event.clientX, y: event.clientY, id: event.pointerId };
      clearTimeout(this.pressTimer);
      this.pressTimer = setTimeout(() => {
        const trigger = root.querySelector('[data-action="message-reactions#open"]');
        if (!trigger?.isConnected) return;
        this.longPressRoot = root;
        this.open({ currentTarget: trigger });
      }, 400);
    };
    this.pressMove = event => {
      if (event.pointerId !== this.pressOrigin?.id) return;
      if (Math.hypot(event.clientX - this.pressOrigin.x, event.clientY - this.pressOrigin.y) > 10) clearTimeout(this.pressTimer);
    };
    this.pressEnd = () => clearTimeout(this.pressTimer);
    this.suppressLongPressClick = event => {
      if (this.longPressRoot?.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.longPressRoot = null;
    };
    this.contextMenu = event => {
      if (this.longPressRoot?.contains(event.target)) event.preventDefault();
    };
    this.list.addEventListener("pointerdown", this.pressStart, { passive: true });
    this.list.addEventListener("contextmenu", this.contextMenu);
    document.addEventListener("pointermove", this.pressMove, { passive: true });
    document.addEventListener("pointerup", this.pressEnd);
    document.addEventListener("pointercancel", this.pressEnd);
    document.addEventListener("click", this.suppressLongPressClick, true);
    this.outside = event => {
      if (!this.menuTarget.hidden && !this.menuTarget.contains(event.target) && !this.anchor?.contains(event.target)) this.close();
    };
    this.keydown = event => {
      const menuOpen = !this.menuTarget.hidden;
      if (!menuOpen && !this.picker?.isPickerVisible()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      } else if (event.key === "Tab" && menuOpen) {
        const buttons = Array.from(this.menuTarget.querySelectorAll("button"));
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    this.scroll = event => {
      this.hideReactionDetails();
      if (!this.menuTarget.hidden && !this.menuTarget.contains(event.target)) this.close(false);
    };
    this.resize = () => this.close(false);
    document.addEventListener("pointerdown", this.outside);
    document.addEventListener("keydown", this.keydown, true);
    document.addEventListener("scroll", this.scroll, true);
    window.addEventListener("resize", this.resize);
  }

  disconnect() {
    this.close(false);
    this.observer.disconnect();
    clearTimeout(this.pressTimer);
    this.list.removeEventListener("pointerdown", this.pressStart);
    this.list.removeEventListener("contextmenu", this.contextMenu);
    document.removeEventListener("pointermove", this.pressMove);
    document.removeEventListener("pointerup", this.pressEnd);
    document.removeEventListener("pointercancel", this.pressEnd);
    document.removeEventListener("click", this.suppressLongPressClick, true);
    this.requests.forEach(request => request.abort());
    this.picker?.destroyPicker();
    clearTimeout(this.feedbackTimer);
    document.removeEventListener("pointerdown", this.outside);
    document.removeEventListener("keydown", this.keydown, true);
    document.removeEventListener("scroll", this.scroll, true);
    window.removeEventListener("resize", this.resize);
  }

  hydrate(node) {
    const bars = node.matches(".chat-reactions") ? [node] : Array.from(node.querySelectorAll(".chat-reactions"));
    bars.forEach(bar => this.renderBar(bar, JSON.parse(bar.dataset.reactions)));
  }

  renderBar(bar, rows) {
    if (this.detailsAnchor && bar.contains(this.detailsAnchor)) this.hideReactionDetails();
    bar.dataset.reactions = JSON.stringify(rows);
    bar.hidden = rows.length === 0;
    const buttons = rows.map(row => {
      const own = row.users.some(user => Number(user.id) === this.userId);
      const names = row.users.map(user => Number(user.id) === this.userId ? "Vous" : user.name).join(", ");
      const button = document.createElement("button");
      button.type = "button";
      button.className = `chat-reaction-chip${own ? " chat-reaction-chip--own" : ""}`;
      button.dataset.action = "click->message-reactions#react mouseenter->message-reactions#showReactionDetails mouseleave->message-reactions#hideReactionDetails focus->message-reactions#showReactionDetails blur->message-reactions#hideReactionDetails keydown.esc->message-reactions#hideReactionDetails";
      button.dataset.emoji = row.emoji;
      button.setAttribute("aria-pressed", String(own));
      button.setAttribute("aria-label", `${row.emoji} : ${names}`);
      const emoji = document.createElement("span");
      emoji.textContent = row.emoji;
      const count = document.createElement("span");
      count.className = "chat-reaction-count";
      count.textContent = row.users.length;
      button.append(emoji, count);
      return button;
    });
    bar.replaceChildren(...buttons);
    if (this.activeRoot === bar.closest(".message_header-container")) this.renderPeople(rows);
  }

  showReactionDetails(event) {
    const anchor = event.currentTarget;
    const rows = JSON.parse(anchor.closest(".chat-reactions").dataset.reactions);
    const row = rows.find(entry => entry.emoji === anchor.dataset.emoji);
    if (!row) return;
    this.hideReactionDetails();
    this.detailsAnchor = anchor;
    const emoji = document.createElement("span");
    emoji.className = "chat-reaction-details__emoji";
    emoji.setAttribute("aria-hidden", "true");
    emoji.textContent = row.emoji;
    const names = document.createElement("span");
    names.textContent = row.users.map(user => Number(user.id) === this.userId ? "Vous" : user.name).join(", ");
    this.detailsTarget.replaceChildren(emoji, names);
    this.positionDetails(anchor);
  }

  showReadDetails(event) {
    const anchor = event.currentTarget;
    this.hideReactionDetails();
    this.detailsAnchor = anchor;
    const name = document.createElement("span");
    name.textContent = anchor.dataset.readerName;
    const date = document.createElement("span");
    date.className = "chat-read-receipt__date";
    date.textContent = anchor.dataset.readLabel;
    this.detailsTarget.replaceChildren(name, date);
    this.positionDetails(anchor);
  }

  positionDetails(anchor) {
    this.detailsTarget.hidden = false;
    anchor.setAttribute("aria-describedby", this.detailsTarget.id);
    const bounds = anchor.getBoundingClientRect();
    const box = this.detailsTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(bounds.left + bounds.width / 2 - box.width / 2, window.innerWidth - box.width - 8));
    const above = bounds.top - box.height - 10 >= 8;
    this.detailsTarget.dataset.placement = above ? "above" : "below";
    this.detailsTarget.style.left = `${left}px`;
    this.detailsTarget.style.top = `${above ? bounds.top - box.height - 10 : Math.max(8, Math.min(bounds.bottom + 10, window.innerHeight - box.height - 8))}px`;
    this.detailsTarget.style.setProperty("--reaction-arrow-left", `${Math.max(14, Math.min(bounds.left + bounds.width / 2 - left, box.width - 14))}px`);
  }

  hideReactionDetails() {
    this.detailsAnchor?.removeAttribute("aria-describedby");
    this.detailsAnchor = null;
    this.detailsTarget.hidden = true;
  }

  open(event) {
    const root = event.currentTarget.closest(".message_header-container");
    if (root.classList.contains("message--deleted")) return;
    if (root === this.activeRoot && !this.menuTarget.hidden) return this.close();
    this.close(false);
    this.activeRoot = root;
    this.anchor = event.currentTarget;
    this.anchor.closest(".message-actions")?.classList.add("message-actions--reacting");
    this.anchor.setAttribute("aria-expanded", "true");
    this.renderPeople(JSON.parse(root.querySelector(".chat-reactions").dataset.reactions));
    this.menuTarget.hidden = false;
    const bounds = this.anchor.getBoundingClientRect();
    const menu = this.menuTarget.getBoundingClientRect();
    this.menuTarget.style.left = `${Math.max(8, Math.min(bounds.right - menu.width, window.innerWidth - menu.width - 8))}px`;
    const top = bounds.top - menu.height - 8;
    this.menuTarget.style.top = `${Math.max(8, top >= 8 ? top : Math.min(bounds.bottom + 8, window.innerHeight - menu.height - 8))}px`;
    this.menuTarget.querySelector("button").focus({ preventScroll: true });
  }

  renderPeople(rows) {
    this.menuTarget.querySelectorAll("[data-emoji]").forEach(button => {
      const row = rows.find(entry => entry.emoji === button.dataset.emoji);
      button.setAttribute("aria-pressed", String(!!row?.users.some(user => Number(user.id) === this.userId)));
    });
    this.peopleTarget.replaceChildren(...rows.map(row => {
      const person = document.createElement("div");
      person.setAttribute("role", "listitem");
      const emoji = document.createElement("span");
      emoji.textContent = row.emoji;
      const names = document.createElement("span");
      names.textContent = row.users.map(user => Number(user.id) === this.userId ? "Vous" : user.name).join(", ");
      person.append(emoji, names);
      return person;
    }));
    this.peopleTarget.hidden = rows.length === 0;
  }

  close(restoreFocus = true) {
    this.hideReactionDetails();
    this.menuTarget.hidden = true;
    if (this.picker?.isPickerVisible()) this.picker.hidePicker();
    if (this.anchor?.isConnected) {
      this.anchor.setAttribute("aria-expanded", "false");
      if (restoreFocus) this.anchor.focus({ preventScroll: true });
      this.anchor.closest(".message-actions")?.classList.remove("message-actions--reacting");
    }
    this.activeRoot = null;
    this.anchor = null;
  }

  choose(event) { this.chooseEmoji(event.currentTarget.dataset.emoji); }

  chooseEmoji(emoji) {
    const root = this.activeRoot;
    this.close();
    if (root?.isConnected) this.mutate(root, emoji);
  }

  react(event) { this.mutate(event.currentTarget.closest(".message_header-container"), event.currentTarget.dataset.emoji); }

  more(event) {
    if (!this.picker) {
      this.picker = new EmojiButton({
        position: "top-end", zIndex: 1100, style: "native", showPreview: false, showAnimation: false,
        i18n: { search: "Rechercher un emoji", notFound: "Aucun emoji trouvé", categories: {
          recents: "Récents", smileys: "Émotions", people: "Personnes", animals: "Animaux",
          food: "Alimentation", activities: "Activités", travel: "Voyages", objects: "Objets",
          symbols: "Symboles", flags: "Drapeaux", custom: "Personnalisés"
        } }
      });
      this.picker.on("emoji", selection => this.chooseEmoji(selection.emoji));
      this.picker.on("hidden", () => {
        if (this.menuTarget.hidden && this.activeRoot) this.close(false);
      });
    }
    this.picker.showPicker(this.anchor);
    this.menuTarget.hidden = true;
  }

  async mutate(root, emoji) {
    const bar = root.querySelector(".chat-reactions");
    if (!bar || bar.dataset.reactionBusy) return;
    const before = JSON.parse(bar.dataset.reactions);
    const version = Number(bar.dataset.reactionsVersion);
    const own = before.find(row => row.users.some(user => Number(user.id) === this.userId));
    const remove = own?.emoji === emoji;
    const optimistic = before.map(row => ({ emoji: row.emoji, users: row.users.filter(user => Number(user.id) !== this.userId) })).filter(row => row.users.length);
    if (!remove) {
      let row = optimistic.find(entry => entry.emoji === emoji);
      if (!row) { row = { emoji, users: [] }; optimistic.push(row); }
      row.users.push({ id: this.userId, name: "Vous" });
    }
    bar.dataset.reactionBusy = "true";
    this.renderBar(bar, optimistic);
    const request = new AbortController();
    this.requests.add(request);
    try {
      const response = await fetch(bar.dataset.reactionsUrl, {
        method: remove ? "DELETE" : "PUT", credentials: "same-origin", signal: request.signal,
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
        body: remove ? undefined : JSON.stringify({ emoji })
      });
      if (!response.ok) throw new Error("reaction_failed");
      this.apply(await response.json());
    } catch (error) {
      if (error.name === "AbortError") return;
      if (bar.isConnected && Number(bar.dataset.reactionsVersion) === version) {
        this.renderBar(bar, before);
        this.feedbackTarget.textContent = "La réaction n’a pas pu être enregistrée. Réessayez.";
        this.feedbackTarget.hidden = false;
        clearTimeout(this.feedbackTimer);
        this.feedbackTimer = setTimeout(() => { this.feedbackTarget.hidden = true; }, 5000);
      }
    } finally {
      delete bar.dataset.reactionBusy;
      this.requests.delete(request);
    }
  }

  receive(event) { this.apply(event.detail); }

  apply(data) {
    const prefix = data.message_type === "MessageHeader" ? "message_header" : data.message_type === "Submessage" ? "submessage" : null;
    if (!prefix) return;
    const root = this.list.querySelector(`#${prefix}-${Number(data.message_id)}`);
    const bar = root?.querySelector(".chat-reactions");
    if (!bar || Number(data.version) <= Number(bar.dataset.reactionsVersion)) return;
    bar.dataset.reactionsVersion = data.version;
    this.renderBar(bar, data.reactions);
  }
}
